import { type FormEvent, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Button, ErrorNote, Field, Input } from "@/components/ui";
import { useAuth } from "@/lib/auth";
import { AuthFrame, messageOf } from "./AuthFrame";

export function SignInScreen() {
  const { status, signIn, resendCode } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [resent, setResent] = useState(false);

  if (status === "signed_in") {
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    return <Navigate to={from} replace />;
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
    } catch (err) {
      const msg = messageOf(err, "Could not sign in.");
      setUnconfirmed(/confirm/i.test(msg));
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame title="Welcome back">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Email">
          <Input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Field>
        <Field label="Password">
          <Input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>
        <ErrorNote message={error} />
        {unconfirmed ? (
          <button
            type="button"
            className="text-left text-[13px] text-accent"
            onClick={() =>
              void resendCode(email.trim()).then(() => setResent(true))
            }
          >
            {resent
              ? "Sent — check your inbox."
              : "Resend the confirmation link"}
          </button>
        ) : null}
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </Button>
      </form>
      <p className="mt-6 text-[13px] text-muted">
        New here?{" "}
        <Link to="/sign-up" className="text-accent">
          Create an account
        </Link>
      </p>
    </AuthFrame>
  );
}
